import PropTypes from 'prop-types'
import React, { Component, Fragment } from 'react'
import ReactDOM from 'react-dom'
import classNames from 'classnames'
import CloseIcon from 'base/components/layout/close_icon'
import Cookies from 'js-cookie'
import { SHOW_GDPR } from 'cookies.yml'
import Button from '@everlane/button'
import { Chevron } from '@everlane/icon'
import CurrentCountryDisplay from 'base/react/components/current_country_display'
import CountrySelectTray from 'mobile/components/trays/country_select_tray'
import CountrySelectModal from 'desktop/components/modals/country_select_modal'
import RouterLink, { EverlaneLink } from 'base/components/router_link'
import EmbeddedDutiesModal from 'base/react/components/embedded_duties_modal'
import {
  embeddedDutiesAware,
  shouldShowEmbeddedDuties,
  determineCountryCode,
} from 'base/lib/embedded_duties_aware'

export class GdprCookieMessageContainer extends Component {
  constructor(props) {
    super(props)
    this.embeddedDutiesModal = React.createRef()
  }
  static propTypes = {
    isMobile: PropTypes.bool,
    dismissedLoginGate: PropTypes.bool,
    dismissedDutiesModal: PropTypes.bool,
  }

  static defaultProps = {
    dismissedLoginGate: false,
    dismissedDutiesModal: false,
  }

  state = {
    showMessage: Cookies.get(SHOW_GDPR) === 'true',
    showCountrySelectTray: false,
  }

  WAIT_MILLISECONDS = 10000

  updateNoScroll() {
    const addNoScroll = !!(this.state.showMessage && this.props.isMobile)
    if (addNoScroll) {
      document.body.classList.add('noscroll')
    } else {
      document.body.classList.remove('noscroll')
    }
  }

  componentDidMount() {
    this.updateNoScroll()
  }

  componentDidUpdate() {
    this.updateNoScroll()
  }

  onClose = () => {
    Cookies.set(SHOW_GDPR, false)
    this.setState({ showMessage: false })
  }

  onDesktopChangeCountryClick = () => {
    this.embeddedDutiesModal.current.handleClose()
    this.onChangeCountryClick()
  }

  onChangeCountryClick = () => {
    this.setState({ showCountrySelectTray: true })
  }

  renderCountrySelectTray() {
    return (
      <CountrySelectTray
        closeCountrySelectTray={() => this.setState({ showCountrySelectTray: false })}
      />
    )
  }

  renderCountrySelectModal() {
    const reactPortalEl = document.getElementById('react-portal')

    return ReactDOM.createPortal(
      <CountrySelectModal onRequestClose={() => this.setState({ showCountrySelectTray: false })} />,
      reactPortalEl,
    )
  }

  renderCountrySelection() {
    return this.props.isMobile ? this.renderCountrySelectTray() : this.renderCountrySelectModal()
  }

  isExpandedContainer() {
    return this.props.isMobile && shouldShowEmbeddedDuties(this.props)
  }

  renderMobileEmbeddedDuties() {
    return (
      <EmbeddedDutiesModal platform="mobile">
        <div className="embedded-duties__geolocation">
          <CurrentCountryDisplay onClickCountryDisplay={this.onChangeCountryClick} />

          <EverlaneLink
            href=""
            onClick={e => {
              e.preventDefault()
              this.onChangeCountryClick()
            }}
            className="embedded-duties__location"
          >
            Change location
            <Chevron />
          </EverlaneLink>
        </div>
      </EmbeddedDutiesModal>
    )
  }

  renderDefaultMobileContent() {
    return (
      <div className="gdpr__country-selection">
        <CurrentCountryDisplay />
        <button
          onClick={this.onChangeCountryClick}
          className="everlane-link everlane-link__chevron gdpr__change-country-link"
        >
          Change location
          <Chevron />
        </button>
      </div>
    )
  }

  renderForMobile() {
    if (shouldShowEmbeddedDuties(this.props)) {
      return this.renderMobileEmbeddedDuties()
    }
    return this.renderDefaultMobileContent()
  }

  renderDesktopEmbeddedDuties() {
    return (
      <EmbeddedDutiesModal
        ref={this.embeddedDutiesModal}
        platform="desktop"
        wait={this.WAIT_MILLISECONDS}
      >
        <div className="embedded-duties__geolocation">
          <CurrentCountryDisplay onClickCountryDisplay={this.onDesktopChangeCountryClick} />

          <EverlaneLink
            href=""
            onClick={e => {
              e.preventDefault()
              this.onDesktopChangeCountryClick()
            }}
            className="embedded-duties__location"
            underlined
          >
            Change location
          </EverlaneLink>
        </div>
      </EmbeddedDutiesModal>
    )
  }

  renderForDesktop() {
    if (
      determineCountryCode(this.props) !== 'US' &&
      this.props.dismissedLoginGate &&
      !this.props.dismissedDutiesModal
    ) {
      return this.renderDesktopEmbeddedDuties()
    }

    return null
  }

  renderEmbeddedDuties() {
    return this.props.isMobile ? this.renderForMobile() : this.renderForDesktop()
  }

  renderGdprDisclaimer() {
    return (
      <p className="gdpr__terms-text">
        We use cookies to optimize your site experience. By interacting with our site, you agree to
        our use of cookies.{' '}
        <RouterLink href="/privacy" className="gdpr__terms-link" target="_blank">
          Learn more
        </RouterLink>
        {this.props.isMobile && (
          <Button className="gdpr__accept-button" onClick={this.onClose} variant="rectangle">
            Accept
          </Button>
        )}
      </p>
    )
  }

  renderGdprMessage() {
    return (
      <Fragment>
        {this.renderGdprDisclaimer()}

        {!this.props.isMobile && (
          <CloseIcon className="gdpr___close-icon" handleClose={this.onClose} />
        )}
      </Fragment>
    )
  }

  render() {
    if (!this.state.showMessage) {
      return null
    }

    return (
      <Fragment>
        {this.props.isMobile && (
          <div onClick={this.onClose} className="gdpr__cookie-message-veil" />
        )}

        <div
          className={classNames('gdpr__cookie-message', {
            'gdpr__cookie-message--expanded': this.isExpandedContainer(),
          })}
        >
          {this.renderEmbeddedDuties()}
          {this.renderGdprMessage()}
        </div>

        {this.state.showCountrySelectTray && this.renderCountrySelection()}
      </Fragment>
    )
  }
}

export default embeddedDutiesAware(GdprCookieMessageContainer)
